import { Injectable, NgZone } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from './firestore-context.service';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import firebase from 'firebase/compat/app';
import 'firebase/compat/messaging';
import { AnalyticsService } from './analytics.service';
import { LanguageService } from './language.service';
import { EventsService } from './events.service';
import { BannerService } from './banner.service';
import { AttractionsService } from './attractions.service';
import { resolveReelText, toReelBanner } from '../models/reel.model';
import { heroImageUrl as eventHeroImageUrl } from '../events/event-utils';
import { heroImageUrl as attractionHeroImageUrl } from '../attractions/attraction-utils';
import {
  TouristicAttractionSlot,
  TouristicFirstSlot,
  TouristicPlan
} from '../models/touristic-notification.model';
import { touristicAnalyticsType } from './touristic-notification-analytics';
import { LanguageSheetService } from './language-sheet.service';
import { ProductSheetService } from './product-sheet.service';
import { ReelService } from './reel.service';

/**
 * The engagement signals that can earn a push ask. Each has its own lifetime slot, per device per
 * venue, so a diner who ignores the Explore-earned ask still gets one Events-earned ask later.
 *
 * 'default' is the fallback for the diner who never opens Explore or Events — most of them, at a
 * venue that is not touristic. It is earned by dwell on the menu itself rather than by a visit to
 * another screen, and it yields to the other two wherever they compete (see `tickDwell`).
 */
export type PushPromptSource = 'explore' | 'events' | 'default';

/** The two a screen visit can earn. 'default' is never routed to — it is earned by dwell. */
type EarnedPushPromptSource = Exclude<PushPromptSource, 'default'>;

/**
 * Everything that can open the permission prompt: the menu's own asks, plus 'order' — asked when the
 * guest taps Order, so they can be told when the venue accepts it. 'order' has no lifetime slot of
 * its own in PROMPT_SOURCES; see `shouldAskForOrderUpdates`.
 */
export type PushAskSource = PushPromptSource | 'order';

const PROMPT_SOURCES: PushPromptSource[] = ['explore', 'events', 'default'];

/** The venue accepted / declined an order — every sender's `notification_type` for those. */
const ORDER_STATUS_TYPES = ['accepted_order', 'delivered_order', 'cancelled_order'];

/**
 * Maps a navigation URL to the engagement it counts as, or null for everywhere else.
 *
 * Exact first-segment match on purpose: `/event/12` is an event DETAIL screen, which a push
 * notification can deep-link into directly, and arriving there is not the diner choosing to browse.
 */
function promptSourceForUrl(url: string): EarnedPushPromptSource | null {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  if (path === '/attractions') return 'explore';
  if (path === '/events') return 'events';
  return null;
}

/**
 * The menu itself, whatever it is carrying. `/menu?event=12` and `/menu?reel=1` are the menu with a
 * notification's destination opening on top of it, not screens of their own.
 */
function isMenuUrl(url: string): boolean {
  return url.split(/[?#]/)[0].replace(/\/+$/, '') === '/menu';
}

@Injectable({
  providedIn: 'root'
})
export class GuestPushService {

  private messagingInstance: firebase.messaging.Messaging | null = null;
  private foregroundHandlerRegistered = false;
  /** In-flight silent init, so concurrent menu loads don't each register a scan. */
  private silentInit: Promise<void> | null = null;
  /** Scan document written this session, kept so a later language choice can correct `language`. */
  private scanDocRef: firebase.firestore.DocumentReference<unknown> | null = null;
  /** `language` currently on that document, so an unchanged choice doesn't write again. */
  private storedLanguage: string | null = null;
  /**
   * Engagement the diner has shown but not yet been asked for. Read by MenuComponent on the next
   * arrival at /menu; cleared when it is spent on an ask.
   *
   * Never holds 'default': that source is not earned by going anywhere, and letting it sit here
   * would make it indistinguishable from a real screen visit at the point the fallback yields.
   */
  private pendingSource: EarnedPushPromptSource | null = null;

  // region Fallback ask — dwell on the menu itself
  /**
   * How long the diner must actively browse the menu before the fallback ask is earned. One number
   * for every venue, touristic or not.
   *
   * 30s is short because the session is: ordering is enabled at only a handful of venues, so for
   * nearly every diner this is a menu to READ, not to order from. There is no ordering step holding
   * the session open at the far end — they scroll, they decide, they put the phone down. A threshold
   * set for a browse-then-order session spends its reach waiting for a step that never comes.
   *
   * It is not shorter than 30s because the ask still has to clear the loading skeleton and land on a
   * diner who has formed an opinion of the menu — the old 5s timer fired before either, which is why
   * it was removed. The engagement floor in `tickDwell` carries the rest of that weight: 30s is the
   * earliest this may fire, not a promise that it will.
   *
   * Every venue is served from ONE origin, so this is the number with the most leverage on the whole
   * feature: a fallback ask dismissed at scale does not merely waste itself — Chrome's crowd-sourced
   * signals push the entire domain into the quiet permission UI, taking the Explore- and
   * Events-earned asks down with it. Move it down further only against real acceptance-rate data.
   */
  private static readonly DEFAULT_ASK_DWELL_MS = 30_000;

  private static readonly DWELL_TICK_MS = 1000;

  /** Menu time accumulated this session, counting only the seconds the menu was actually in view. */
  private dwellMs = 0;
  private dwellTimer?: ReturnType<typeof setInterval>;
  /** Whether /menu is the current route. Maintained from the one router subscription below. */
  private onMenuRoute = false;
  /**
   * The engagement floor: has the diner done one deliberate thing on the menu — scrolled past the
   * fold, tapped a category, opened a product? Session-lived, like the dwell it gates.
   */
  private hasBrowsedMenu = false;
  private readonly defaultAsk = new Subject<void>();

  /**
   * Fires once when the fallback ask comes due. MenuComponent opens the prompt on it; the budget is
   * already checked by the time this emits.
   */
  readonly defaultAskDue$: Observable<void> = this.defaultAsk.asObservable();
  // endregion

  private readonly orderStatusPush = new Subject<number>();

  /**
   * The order id of an accepted / declined push that arrived while this page was in front, so the
   * order screen can re-read that order's status. A push that lands with the tab hidden goes to
   * the service worker instead and is never seen here; the order screen re-reads on its return to
   * the tab for that one.
   */
  readonly orderStatusPush$: Observable<number> = this.orderStatusPush.asObservable();

  /**
   * Bound on the whole touristic resolve. Three lookups now sit between "we have a token" and the
   * subscription write, and one of them is an HTTP call — a venue whose backend is having a bad
   * minute must still get its scan document, just without the notifications it could not describe.
   */
  private static readonly TOURISTIC_RESOLVE_TIMEOUT_MS = 4000;

  /**
   * True while a scan write is being prepared. `hasRegisteredScan()` only becomes true once the
   * document exists, and the touristic resolve now holds that gap open for up to four seconds —
   * long enough for a second caller to slip through and register the same visit twice.
   */
  private scanWriteInFlight = false;

  constructor(
    private afs: AngularFirestore,
    private fsContext: FirestoreContextService,
    private analytics: AnalyticsService,
    private languageService: LanguageService,
    private eventsService: EventsService,
    private bannerService: BannerService,
    private attractionsService: AttractionsService,
    private router: Router,
    private ngZone: NgZone,
    private languageSheet: LanguageSheetService,
    private productSheet: ProductSheetService,
    private reel: ReelService
  ) {
    // On the silent re-subscribe path the scan document is written while the first-visit language
    // sheet is still on screen, so it carries the venue default rather than the diner's choice —
    // as does any switch made later from the header flag. `onGuestScanned` fires on create only and
    // `sendPromotion` re-reads `language` at send time, so patching the existing document
    // re-languages the pending notification without scheduling a second one.
    this.languageService.languageChosen$.subscribe(lang => this.updateStoredLanguage(lang));

    // One app-lived subscription, and it has to live here rather than on MenuComponent: the router
    // destroys and rebuilds MenuComponent on every return to the menu, so the component cannot be
    // the thing that remembers where the diner just was. This service is created on the first menu
    // load, which always precedes any Explore / Events visit.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.onNavigation(event.urlAfterRedirects));
  }

  private onNavigation(url: string): void {
    // The dwell clock behind the fallback ask only runs on the menu. Kept here rather than read off
    // the Router on every tick so the tick stays a plain field read.
    this.onMenuRoute = isMenuUrl(url);
    this.recordEngagement(url);
  }

  /**
   * Records a visit to Explore or Events as the reason for the next ask.
   *
   * A source whose lifetime slot is already spent is ignored rather than stored, so it cannot
   * clobber a pending one that still has budget: Explore → Events → menu still produces the
   * Events-earned ask on a device that used up Explore on an earlier visit.
   */
  private recordEngagement(url: string): void {
    const source = promptSourceForUrl(url);
    if (!source || this.isSourceSpent(source)) return;
    this.pendingSource = source;
  }

  /** The engagement the next ask would be attributable to, if the diner has shown one. */
  pendingPromptSource(): EarnedPushPromptSource | null {
    return this.pendingSource;
  }

  /**
   * One deliberate move on the menu: scrolled past the fold, or tapped a category. Opening a product
   * counts too, but `tickDwell` reads that one straight off ProductSheetService.
   *
   * Idempotent, and called from a scroll handler — keep it a field write.
   */
  noteMenuInteraction(): void {
    this.hasBrowsedMenu = true;
  }

  /**
   * Starts the dwell clock behind the fallback ask.
   *
   * Idempotent by design: `handleMenuOpen` runs on every arrival at /menu and the router rebuilds
   * MenuComponent each time, so this is called many times a visit and must keep ONE clock. The
   * accumulator lives on the service for the same reason `pendingSource` does — the component is not
   * around long enough to hold it.
   */
  private armDefaultAsk(): void {
    if (this.dwellTimer) return;
    // Outside the zone deliberately: a bare setInterval inside it would run change detection over
    // the entire menu once a second for the length of the session. The one emission that matters is
    // handed back with ngZone.run below.
    this.ngZone.runOutsideAngular(() => {
      this.dwellTimer = setInterval(() => this.tickDwell(), GuestPushService.DWELL_TICK_MS);
    });
  }

  private stopDefaultAsk(): void {
    clearInterval(this.dwellTimer);
    this.dwellTimer = undefined;
  }

  /**
   * True only when the menu itself is what the diner is looking at: tab in the foreground, /menu the
   * route, nothing stacked on top. Each of those is a state where a permission dialog would arrive
   * with no context behind it — a pocketed phone, an Explore screen, an open sheet — so none of them
   * count toward the dwell that earns the ask.
   */
  private isMenuInView(): boolean {
    if (document.hidden) return false;
    if (!this.onMenuRoute) return false;
    return !this.languageSheet.isOpen && !this.productSheet.isOpen && !this.reel.isOpen;
  }

  /** One second of menu time, counted only if the diner actually spent it on the menu. */
  private tickDwell(): void {
    // An open product sheet is the strongest "this menu is useful to me" signal there is, and it is
    // readable synchronously — no subscription needed. It does not add to the dwell (isMenuInView
    // rejects it: the sheet is what's on screen, not the menu), it only clears the floor.
    if (this.productSheet.isOpen) this.hasBrowsedMenu = true;

    if (!this.isMenuInView()) return;

    this.dwellMs += GuestPushService.DWELL_TICK_MS;
    if (this.dwellMs < GuestPushService.DEFAULT_ASK_DWELL_MS) return;

    // The fallback always yields. A pending earned source means MenuComponent is about to open a
    // better-converting, better-attributed ask two seconds from now, and the visit has one to spend.
    // Not a stop: the diner may never return to /menu to collect it, in which case the dwell keeps
    // running and this ask is still here.
    //
    // This only catches Explore and Events the diner has ALREADY seen. At the same 30s everywhere,
    // a touristic diner who browses the menu first and opens Explore afterwards gets the fallback,
    // and the earned ask waits for their next visit — the accepted cost of one number.
    if (this.pendingSource) return;

    // The engagement floor, and it carries more weight at 30s than it did at 45: a diner sitting on
    // a loaded menu having touched nothing is the old timer wearing a longer fuse, and the shorter
    // the dwell the more of them there are. No upper bound is needed — the per-visit cap and the
    // lifetime slot already bound how often this can happen at all.
    if (!this.hasBrowsedMenu) return;

    // Every remaining reason to refuse is settled for the whole session — permission answered
    // elsewhere, budget gone, or this visit has already asked — so this one does stop the clock.
    if (!this.shouldShowPrompt('default')) {
      this.stopDefaultAsk();
      return;
    }

    // Deliberately NOT stopped here. The router destroys MenuComponent before it emits the
    // NavigationEnd that clears `onMenuRoute`, so there is a sliver in which this fires with nobody
    // subscribed; stopping first would lose the ask for the whole session, silently, with the slot
    // still unspent. `markPromptShown` stops the clock instead — it runs synchronously inside the
    // subscriber — so an emission that lands on nothing simply comes round again next tick.
    this.ngZone.run(() => this.defaultAsk.next());
  }

  isAndroid(): boolean {
    return /Android/i.test(navigator.userAgent);
  }

  isPushSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * In dev: any browser that supports push. In prod: any Android browser that supports push.
   */
  isEligible(): boolean {
    if (!this.isPushSupported()) return false;
    if (environment.production && !this.isAndroid()) return false;
    return true;
  }

  /**
   * Has the user already granted notification permission?
   */
  isPermissionGranted(): boolean {
    return 'Notification' in window && Notification.permission === 'granted';
  }

  /**
   * Should we show the permission prompt for an ask earned by `source`?
   * True only if: eligible platform, permission not yet decided, this visit has not asked yet, and
   * `source` still has its lifetime slot.
   */
  shouldShowPrompt(source: PushPromptSource): boolean {
    if (!this.isEligible()) return false;
    // Notification.permission's OWN 'default' — the browser's word for undecided, and no relation to
    // the PushPromptSource of the same name. Anything else is already an answer: granted needs no
    // ask, denied cannot be re-asked by us anyway.
    if (Notification.permission !== 'default') return false;

    const clientId = sessionStorage.getItem('clientId');
    if (!clientId) return false;

    if (this.hasPromptedThisVisit()) return false;
    return !this.isSourceSpent(source);
  }

  /**
   * Spends `source`'s lifetime slot plus this visit's single ask, and logs the impression.
   *
   * Called as the prompt goes ON SCREEN, not when the diner answers it. An ask the diner walks away
   * from is still an ask, and pinning the spend to an answer would let an ignored prompt come back
   * on every later visit.
   */
  markPromptShown(source: PushPromptSource): void {
    const clientId = sessionStorage.getItem('clientId');
    if (!clientId) return;

    const spent = this.spentSources();
    if (!spent.includes(source)) {
      localStorage.setItem(this.spentSourcesKey(clientId), JSON.stringify([...spent, source]));
    }
    sessionStorage.setItem(this.visitPromptKey(clientId), 'true');
    this.pendingSource = null;
    // Whichever source got here, the visit's single ask is now spent — so is any reason to keep
    // counting menu time. An Explore-earned prompt stops the fallback clock as surely as the
    // fallback's own does.
    this.stopDefaultAsk();

    // The impression this funnel had no denominator for: `push_prompt_enable_tapped` and the
    // permission events had nothing to divide into, so the acceptance rate was uncomputable.
    // `prompt_source`, NOT `source` — that name is a gtag-reserved traffic-source field and is
    // already in the base bundle as the platform (see the AnalyticsService class note).
    this.analytics.log('push_prompt_shown', { prompt_source: source });
  }

  /**
   * An explicit block in the browser's own dialog. `Notification.permission` is 'denied' from here
   * on, so shouldShowPrompt() already stops everything; burning the remaining slots as well keeps
   * the refusal recorded if the diner later resets the site permission back to 'default'. The
   * fallback slot goes with them — a diner who blocked us has not asked to be asked again on dwell.
   */
  markPromptRefused(): void {
    const clientId = sessionStorage.getItem('clientId');
    if (clientId) {
      localStorage.setItem(this.spentSourcesKey(clientId), JSON.stringify(PROMPT_SOURCES));
    }
  }

  // region Order-status ask — asked on Order, answered before the order goes out
  /**
   * Should tapping Order first ask for permission to notify the guest about their order?
   *
   * Kept apart from the menu prompt's budget on purpose: it is a different question ("tell me when
   * MY order is accepted"), asked at the one moment it obviously makes sense, so an ignored menu
   * prompt earlier in the visit must not use it up. It still respects the browser's answer, and a
   * "Not now" here is final for this venue — the guest will order again, and being asked on every
   * order would be nagging.
   */
  shouldAskForOrderUpdates(): boolean {
    if (!this.isEligible()) return false;
    if (Notification.permission !== 'default') return false;

    const clientId = sessionStorage.getItem('clientId');
    if (!clientId) return false;

    // Closing the browser dialog without answering leaves the permission at 'default'; the
    // session flag stops the next tap (a retry, the corrected-total confirm) asking straight again.
    if (sessionStorage.getItem(this.orderAskShownKey(clientId)) === 'true') return false;
    return localStorage.getItem(this.orderAskDeclinedKey(clientId)) !== 'true';
  }

  /**
   * The order ask is on screen. Also takes this visit's single menu ask, so a "Not now" here is not
   * followed by the menu's own banner a minute later — but not a menu source's lifetime slot.
   */
  markOrderAskShown(): void {
    const clientId = sessionStorage.getItem('clientId');
    if (!clientId) return;
    sessionStorage.setItem(this.orderAskShownKey(clientId), 'true');
    sessionStorage.setItem(this.visitPromptKey(clientId), 'true');
    this.pendingSource = null;
    this.stopDefaultAsk();
    this.analytics.log('push_prompt_shown', { prompt_source: 'order' });
  }

  /** "Not now" on the order ask. */
  markOrderAskDeclined(): void {
    const clientId = sessionStorage.getItem('clientId');
    if (clientId) localStorage.setItem(this.orderAskDeclinedKey(clientId), 'true');
  }

  /**
   * The browser's permission dialog, for the order ask. Unlike `requestPermissionAndSubscribe` this
   * registers no scan: the guest asked to hear about their order, and the token goes on the order.
   * (A guest who allows will still be registered on their next menu load, like any guest who has.)
   */
  async requestPermissionForOrder(): Promise<boolean> {
    try {
      await this.registerPushWorker();
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this.analytics.log('push_permission_denied', { permission_state: permission, prompt_source: 'order' });
        if (permission === 'denied') this.markPromptRefused();
        return false;
      }
      this.analytics.log('push_permission_granted', { prompt_source: 'order' });
      return true;
    } catch (error) {
      console.error('GuestPushService: order permission request failed:', error);
      return false;
    }
  }

  /**
   * This device's FCM token for the order's `user_token`, or null when it cannot have one: not
   * eligible, not allowed, or the token did not arrive in time. Bounded because the order waits on
   * it — an order without a token is still an order, a hung Order button is not.
   *
   * Also sets up the foreground handler: without it, a status push arriving while this tab is in
   * front is delivered to the page and shown by nobody.
   */
  async orderPushToken(): Promise<string | null> {
    if (!this.isEligible() || !this.isPermissionGranted()) return null;

    const fetchToken = async (): Promise<string | null> => {
      const registration = await this.registerPushWorker();
      const messaging = this.getMessaging();
      const token = await messaging.getToken({
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: registration
      });
      if (token) this.setupForegroundHandler(messaging);
      return token || null;
    };

    try {
      return await Promise.race([
        fetchToken(),
        new Promise<null>(resolve => setTimeout(() => resolve(null), GuestPushService.ORDER_TOKEN_TIMEOUT_MS))
      ]);
    } catch (error) {
      console.error('GuestPushService: no push token for the order:', error);
      return null;
    }
  }

  private static readonly ORDER_TOKEN_TIMEOUT_MS = 4000;

  private orderAskShownKey(clientId: string): string {
    return `pushOrderAskShown_${clientId}`;
  }

  private orderAskDeclinedKey(clientId: string): string {
    return `pushOrderAskDeclined_${clientId}`;
  }
  // endregion

  /** Lifetime budget: which sources have already produced an ask on this device, for this venue. */
  private spentSourcesKey(clientId: string): string {
    return `pushPromptSpentSources_${clientId}`;
  }

  /**
   * Per-visit budget: one ask per QR session however much the diner browses. Session-scoped, and
   * keyed per venue like the lifetime record — a genuine re-scan is a new visit and may ask again,
   * if a source still has budget.
   */
  private visitPromptKey(clientId: string): string {
    return `pushPromptShown_${clientId}`;
  }

  private spentSources(): PushPromptSource[] {
    const clientId = sessionStorage.getItem('clientId');
    if (!clientId) return [];
    try {
      const raw = JSON.parse(localStorage.getItem(this.spentSourcesKey(clientId)) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw.filter((value): value is PushPromptSource => PROMPT_SOURCES.includes(value));
    } catch {
      // Hand-edited or half-written storage reads as "nothing spent" rather than throwing on every
      // navigation — the per-visit cap still holds the damage to one ask.
      return [];
    }
  }

  private isSourceSpent(source: PushPromptSource): boolean {
    return this.spentSources().includes(source);
  }

  private hasPromptedThisVisit(): boolean {
    const clientId = sessionStorage.getItem('clientId');
    return !!clientId && sessionStorage.getItem(this.visitPromptKey(clientId)) === 'true';
  }

  /**
   * Called on every menu load (every QR scan).
   * - If permission already granted → silently schedule re-engagement
   * - If permission not yet asked → returns false (caller should show prompt)
   * - If permission denied → do nothing
   *
   * MenuComponent is re-created on every navigation back to home, so this runs many times per
   * visit; only the first one registers a scan (see `hasRegisteredScan`).
   */
  async handleMenuOpen(): Promise<'scheduled' | 'already_scheduled' | 'needs_prompt' | 'ineligible'> {
    if (!this.isEligible()) return 'ineligible';

    if (this.isPermissionGranted()) {
      // Already allowed from a previous visit — silently schedule
      const alreadyScheduled = this.hasRegisteredScan();
      if (!this.silentInit) {
        this.silentInit = this.subscribeAndSchedule();
      }
      await this.silentInit;
      return alreadyScheduled ? 'already_scheduled' : 'scheduled';
    }

    if (Notification.permission === 'denied') return 'ineligible';

    // Permission is 'default' — needs prompt. Start the dwell clock behind the fallback ask now,
    // whether or not this arrival carries an earned source: the diner who never leaves the menu is
    // exactly the one the fallback exists for, and `tickDwell` re-checks everything before firing.
    this.armDefaultAsk();
    return 'needs_prompt';
  }

  /**
   * Called when user taps "Enable" on our prompt.
   * Triggers browser permission dialog, then subscribes.
   */
  async requestPermissionAndSubscribe(): Promise<boolean> {
    try {
      const registration = await this.registerPushWorker();

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        // `denied` is an explicit block; `default` means the diner closed the browser dialog without
        // answering. Both end the funnel here, but only the first is a real refusal — keep them
        // apart in `permission_state` so the two don't read as one number.
        this.analytics.log('push_permission_denied', { permission_state: permission });
        // Only the refusal spends budget. On 'default' the diner closed the dialog without deciding
        // and the permission is untouched, so treating it as a no used to burn the whole feature for
        // that device. The ask that got here has already spent its own source's slot; the diner's
        // other source keeps its one remaining ask.
        if (permission === 'denied') this.markPromptRefused();
        return false;
      }

      this.analytics.log('push_permission_granted');

      const messaging = this.getMessaging();
      const token = await messaging.getToken({
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: registration
      });

      if (!token) {
        console.warn('GuestPushService: No FCM token received');
        return false;
      }

      console.log('GuestPushService: FCM token retrieved:', token);

      await this.storeSubscription(token);
      this.setupForegroundHandler(messaging);

      return true;
    } catch (error) {
      console.error('GuestPushService: Error subscribing to push:', error);
      return false;
    }
  }

  /**
   * Silent subscribe + schedule for returning users who already granted permission.
   */
  private async subscribeAndSchedule(): Promise<void> {
    try {
      const registration = await this.registerPushWorker();

      const messaging = this.getMessaging();
      const token = await messaging.getToken({
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: registration
      });

      if (!token) {
        this.silentInit = null;
        return;
      }

      console.log('GuestPushService: Silent re-subscribe, token:', token);

      await this.storeSubscription(token);
      this.setupForegroundHandler(messaging);
    } catch (error) {
      console.error('GuestPushService: Silent subscribe error:', error);
      // Drop the cached attempt so the next menu load can retry.
      this.silentInit = null;
    }
  }

  /**
   * Key marking that this QR session has already produced a scan document. Session-scoped, so a
   * genuine re-scan (new tab / new venue) still schedules, while in-app navigation and reloads
   * within the same session do not.
   */
  private scanKey(): string | null {
    const clientId = sessionStorage.getItem('clientId');
    if (!clientId) return null;
    return `pushScanRegistered_${clientId}_${sessionStorage.getItem('tableId') || ''}`;
  }

  private hasRegisteredScan(): boolean {
    const key = this.scanKey();
    return !!key && sessionStorage.getItem(key) === 'true';
  }

  /**
   * Stores or updates the push subscription in Firestore.
   * Every write (create or update) triggers the Cloud Function to schedule a notification, so this
   * writes at most once per QR session — otherwise every return to the menu re-notifies the guest.
   */
  private async storeSubscription(fcmToken: string): Promise<void> {
    if (this.hasRegisteredScan() || this.scanWriteInFlight) return;

    const deviceGuid = localStorage.getItem('deviceGuid');
    const clientId = sessionStorage.getItem('clientId');
    const tableId = sessionStorage.getItem('tableId');
    const clientDataString = sessionStorage.getItem('clientData');
    const clientData = clientDataString ? JSON.parse(clientDataString) : null;

    if (!deviceGuid || !clientId) {
      console.error('GuestPushService: Missing deviceGuid:', deviceGuid, 'clientId:', clientId);
      return;
    }

    this.scanWriteInFlight = true;
    try {
      const language = this.resolveLanguage();

      // A touristic venue sends the event / Gulli pair described in `touristic-notification.model.ts`
      // INSTEAD of its scheduled promotions, so this flag has to be written even when the plan comes
      // back empty — it is what tells `onGuestScanned` which of the two pipelines this scan belongs
      // to, and an empty plan must mean "touristic venue with nothing to say" rather than "not
      // touristic".
      const isTouristic = clientData?.is_touristic === true;
      const touristic = isTouristic ? await this.resolveTouristicPlan(clientData) : null;

      // Write to a scan-based collection — each scan creates a new document
      // This triggers the Cloud Function every time
      const scanDoc = {
        deviceGuid,
        fcmToken,
        clientId,
        language,
        tableId: tableId || null,
        shopName: clientData?.name || null,
        shopLogoUrl: clientData?.catalogue_icon || null,
        scannedAt: firebase.firestore.FieldValue.serverTimestamp(),
        platform: 'web-android',
        promotionSentAt: null,
        isTouristic,
        touristic,
        // Read by `sendTouristicNotification` to decide whether the notification is landing in the
        // guest's small hours and should arrive silently. The diner's own zone, not the venue's: it
        // is their phone that will light up, and a guest who flew in yesterday is still on it.
        ...this.deviceTimeZoneFields()
      };

      this.scanDocRef = await this.fsContext
        .run(() => this.afs.collection('guest-push-subscriptions'))
        .add(scanDoc);
      this.storedLanguage = language;

      // Only after the write succeeds, mirroring iOS, which logs inside the completion of
      // `UNUserNotificationCenter.add` and only on its success branch. A failed registration must
      // not report a notification that was never scheduled.
      this.logTouristicSchedule(touristic);

      const key = this.scanKey();
      if (key) sessionStorage.setItem(key, 'true');

      console.log('GuestPushService: Scan registered, notification scheduled');

      // A language picked while this write was in flight had no document to patch yet — reconcile.
      await this.updateStoredLanguage(this.resolveLanguage());
    } finally {
      this.scanWriteInFlight = false;
    }
  }

  /**
   * Report the notifications this scan actually armed — one event per NON-NULL slot.
   *
   * ONE PER SLOT, NEVER ONE PER SCAN. A null slot means the venue had nothing to say in it (no event
   * today and no media banner, or no attractions at all), and the model is explicit that this
   * "schedules nothing; it is not an error". Counting an empty slot as sent would deflate the open
   * rate on the dashboard by exactly the share of venues with nothing to promote.
   *
   * ⚠️ WHAT "SENT" MEANS HERE IS WEAKER THAN ON iOS, and the dashboard says so. iOS schedules a LOCAL
   * notification, so an arm is all but a delivery. This writes a plan that `sendTouristicNotification`
   * decides on later and re-checks for liveness first, so a deleted event means no send at all. The
   * platform split on the Notifications card exists to keep the two from being read as one number.
   *
   * The once-per-visit property is inherited, not added: `storeSubscription` already guards on
   * `hasRegisteredScan()` / `scanWriteInFlight` and writes at most once per QR session. Deliberately
   * NOT the `pushPromptShown_*` / `pushPromptSpentSources_*` keys — those govern the prompt, which is
   * a different question from what was scheduled.
   */
  private logTouristicSchedule(plan: TouristicPlan | null): void {
    if (!plan) {
      return;
    }

    if (plan.first) {
      this.analytics.log('touristic_notification_schedule', {
        type: touristicAnalyticsType(plan.first.kind),
        timing_type: 'immediate',
        id: plan.first.kind === 'media' ? plan.first.bannerId : plan.first.eventId
      });
    }

    if (plan.second) {
      this.analytics.log('touristic_notification_schedule', {
        type: 'attraction',
        timing_type: 'delayed',
        id: plan.second.code
      });
    }
  }

  /**
   * The diner's timezone as two independent readings, because neither is guaranteed on its own: the
   * IANA name is what a server can reason about across DST, and the raw offset is the fallback for a
   * browser whose `Intl` has no zone to report (and the only one an ancient WebView will produce).
   *
   * `getTimezoneOffset()` is UTC-minus-local in minutes, so it is NEGATIVE east of Greenwich — the
   * function subtracts it rather than adding. Sign errors here are silent: they only ever show up as
   * a notification that pinged at 03:00.
   */
  private deviceTimeZoneFields(): { timeZone: string | null; timeZoneOffsetMinutes: number } {
    let timeZone: string | null = null;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      timeZone = null;
    }
    return { timeZone, timeZoneOffsetMinutes: new Date().getTimezoneOffset() };
  }

  /**
   * What this venue's two notifications will say — see `touristic-notification.model.ts` for why the
   * client resolves this rather than the Cloud Function.
   *
   * The two slots are independent and resolved concurrently: a venue with no attractions still gets
   * its event notification, and a venue whose Explore catalogue is down still gets the other one.
   * Each is separately bounded, so one slow lookup cannot spend the other's budget, and a failure of
   * either resolves to `null` rather than rejecting — the subscription itself must survive.
   */
  private async resolveTouristicPlan(clientData: any): Promise<TouristicPlan> {
    const shopId = clientData?.id;
    if (!shopId) return { first: null, second: null };

    const bounded = <T>(work: Promise<T | null>): Promise<T | null> => Promise.race([
      work.catch(error => {
        console.error('GuestPushService: touristic slot failed to resolve:', error);
        return null;
      }),
      new Promise<null>(resolve =>
        setTimeout(() => resolve(null), GuestPushService.TOURISTIC_RESOLVE_TIMEOUT_MS)
      )
    ]);

    const [first, second] = await Promise.all([
      bounded(this.resolveTouristicFirstSlot(shopId)),
      bounded(this.resolveTouristicAttractionSlot(shopId))
    ]);

    return { first: first ?? null, second: second ?? null };
  }

  /**
   * Today's event if the venue has one, otherwise its active media banner — the Gulli reel.
   *
   * Order is the rule, not a preference: the banner is the fallback for a day with nothing on, so it
   * is only ever looked up once the event query has come back empty. A venue with neither returns
   * null and simply sends nothing at the 50-minute mark.
   *
   * Text is resolved through the SAME helpers the screens use — `resolveReelText` for the banner, the
   * raw `title` for the event (event documents carry no translations at all) — so the notification
   * and the screen it opens can never word the same thing differently.
   */
  private async resolveTouristicFirstSlot(shopId: number | string): Promise<TouristicFirstSlot | null> {
    const event = await this.eventsService.getTodaysEvent(shopId);
    const eventId = (event?.id ?? '').trim();
    const eventTitle = (event?.title ?? '').trim();
    if (event && eventId && eventTitle) {
      return { kind: 'event', eventId, title: eventTitle, image: eventHeroImageUrl(event) ?? '' };
    }

    // Cached per session by BannerService, so on the common path this costs nothing: the menu has
    // already fetched the very banner the diner can see behind the permission prompt.
    const banner = await this.bannerService.fetchActiveBanner(Number(shopId));
    if (!banner || banner.type !== 'media' || !banner.id) return null;

    // Empty fallback on purpose: with no `detailTitle` at all there is no sentence to send, and a
    // notification whose body is a placeholder is worse than one that never arrives.
    const title = resolveReelText(banner.detailTitle, this.languageService.getCurrentLang(), '').trim();
    if (!title) return null;

    // A reel with no playable clip opens NOTHING — `toReelBanner` drops every item with a blank
    // `mediaUrl` and `ReelService.open` is inert for an empty one. Advertising it would put a
    // notification in front of the guest whose tap dead-ends on the menu, which is the same reason
    // the attraction slot below refuses an attraction with no code.
    if (!toReelBanner(banner)?.clips.length) return null;

    return { kind: 'media', bannerId: banner.id, title, image: (banner.imageUrl ?? '').trim() };
  }

  /**
   * The venue's highlighted attraction, falling back to the first of its curated picks — the same
   * resolution order the Explore hero uses (`AttractionsListComponent.resolveHeroAttraction`), so the
   * notification names the attraction the diner already saw on the home banner.
   *
   * Titles arrive from the provider in en-US for every guest, which is exactly what the Explore list
   * and the details sheet show, so nothing is localized here and nothing should be.
   */
  private async resolveTouristicAttractionSlot(shopId: number | string): Promise<TouristicAttractionSlot | null> {
    const response = await this.attractionsService.getRecommended(shopId);
    const items = response?.items ?? [];
    if (!items.length) return null;

    const attraction = items.find(item => item.is_highlighted === true) ?? items[0];
    const code = (attraction?.provider_product_id ?? '').trim();
    const title = (attraction?.title ?? '').trim();
    // The details sheet is keyed on the code, so an attraction the provider gave no id for cannot be
    // opened and must not be advertised.
    if (!code || !title) return null;

    return { code, title, image: attractionHeroImageUrl(attraction) ?? '' };
  }

  /**
   * Language the diner is reading the menu in, so the Cloud Function can pick the matching
   * promotion translation server-side (the service worker has no access to app state).
   * Only trustworthy once the venue has reported its languages: with no `languages` array
   * LanguageService is still on its 'en' default, which is indistinguishable from a real
   * English choice. Send null there instead — the function then uses the shop's own default
   * text, which is what this diner is already reading. A wrong language is worse than none.
   */
  private resolveLanguage(): string | null {
    return this.languageService.getVenueLanguages().length
      ? this.languageService.getCurrentLang()
      : null;
  }

  /**
   * Corrects `language` on this session's scan document when the diner picks a language after it
   * was written. No-op before the document exists (the write itself then carries the choice) and
   * when nothing changed.
   */
  private async updateStoredLanguage(language: string | null): Promise<void> {
    if (!this.scanDocRef || !language || language === this.storedLanguage) return;

    this.storedLanguage = language;
    try {
      await this.scanDocRef.update({ language });
      console.log('GuestPushService: Scan language updated to', language);
    } catch (error) {
      console.error('GuestPushService: Failed to update scan language:', error);
    }
  }

  /**
   * Register the push worker, handing it this build's Firebase config in its query string.
   *
   * The worker used to hardcode its own copy, in two files swapped by an angular.json
   * `fileReplacements` entry — a swap that never fired, because fileReplacements does not reach
   * files copied by the assets pipeline. Production therefore served the development config from
   * the day push shipped. Passing the config here means there is a single source of truth and the
   * worker cannot disagree with the app, whatever built or deployed it.
   *
   * Scope is derived from the path, not the query, so this updates any existing registration in
   * place rather than creating a second one — the push subscription and FCM token survive.
   */
  private registerPushWorker(): Promise<ServiceWorkerRegistration> {
    const config = new URLSearchParams(
      Object.entries(environment.firebaseConfig).map(([key, value]) => [key, String(value)])
    );
    return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${config.toString()}`);
  }

  private getMessaging(): firebase.messaging.Messaging {
    if (!this.messagingInstance) {
      this.messagingInstance = firebase.messaging();
    }
    return this.messagingInstance;
  }

  private setupForegroundHandler(messaging: firebase.messaging.Messaging): void {
    if (this.foregroundHandlerRegistered) return;
    this.foregroundHandlerRegistered = true;

    messaging.onMessage(async (payload) => {
      const rawTitle = payload.data?.title || payload.notification?.title || '';
      const rawBody = payload.data?.body || payload.notification?.body || '';
      const image = payload.data?.image || payload.notification?.image;
      const shopName = payload.data?.shop_name || '';
      const shopLogoUrl = payload.data?.shop_logo_url || '';

      const statusOrderId = Number(payload.data?.['order_id']);
      if (statusOrderId && ORDER_STATUS_TYPES.includes(payload.data?.notification_type || '')) {
        this.ngZone.run(() => this.orderStatusPush.next(statusOrderId));
      }

      // Foreground only — a notification that arrives while the tab is closed is delivered by
      // firebase-messaging-sw.js, which has no access to this service and reports nothing.
      this.analytics.log('push_notification_received', {
        notification_type: payload.data?.notification_type || 'default',
        // Which of the three touristic notifications this is (event / media / attraction). Absent on
        // every other payload, and `clean()` drops it there rather than sending an empty dimension.
        touristic_kind: payload.data?.['touristic_kind']
      });

      // Browser Notifications API has no `subtitle` — merge the raw payload
      // title into the body as a first line when we hoist shop_name into the
      // title. The 'Feather Menu' fallback applies only to the display title;
      // never to the body, so legacy payloads don't get a stray placeholder
      // line.
      const displayTitle = shopName || rawTitle || 'Feather Menu';
      const displayBody = shopName && rawTitle ? `${rawTitle}\n${rawBody}` : rawBody;
      const displayIcon = shopLogoUrl || '/assets/logo.png';

      const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      if (!registration) return;

      // A shop can send an immediate AND a delayed promotion for the same visit, and both carry
      // notification_type 'promotion'. Matching tags REPLACE each other in the tray, so a shared
      // tag means the delayed one silently wipes an immediate promotion the guest hadn't opened
      // yet. One tag per timing bucket keeps them independent while still collapsing a repeat of
      // the same bucket. Payloads without a timing_type keep exactly the tag they have today.
      const notificationType = payload.data?.notification_type || 'default';
      const timingType = payload.data?.['timing_type'];
      // Per order, as in firebase-messaging-sw.js — keep the two in lockstep.
      const orderId = payload.data?.['order_id'];
      const tag = orderId
        ? `order_${orderId}`
        : timingType ? `${notificationType}_${timingType}` : notificationType;

      // Quiet hours: the sender decided this one lands in the guest's small hours, so it arrives
      // without a sound or a vibration and waits for them in the tray. Deliberately a string
      // comparison — FCM data values are always strings, so a boolean `false` would arrive as
      // "false" and be truthy. Never set `vibrate` alongside this: Chrome throws a TypeError when a
      // silent notification also asks to buzz.
      const silent = payload.data?.['silent'] === 'true';

      // `image` and `renotify` are part of the Notifications API spec but
       // missing from lib.dom.d.ts; cast to bypass the strict check.
      await registration.showNotification(displayTitle, {
        body: displayBody,
        icon: displayIcon,
        badge: '/assets/logo.png',
        image,
        data: payload.data || {},
        tag,
        renotify: true,
        silent,
      } as NotificationOptions);
    });
  }
}
